import { ApplicantTabNavigation } from '@components/layout/ApplicantTabNavigation'
import { ApplicationHeader } from '@components/layout/ApplicationHeader'
import { getApplicationHeaderProps } from '@lib/utils/applicationHeaderHelpers'
import { AlertCircle, Loader2, Lock } from 'lucide-react'
import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { getTabPathWithApplicantId, getTabNavigationOptions, validateApplicationForPayment, TAB_PATHS, type ApplicationTab } from '@lib/utils/applicationNavigation'
import { useAppSelector, useAppDispatch } from '@/app/hooks'
import { selectApplicationData, selectIsReadOnly, setApplicationData, fetchApplications, fetchApplicationById } from '@/features/applicant/applicationSlice'
import { PaymentForm, PaymentLogTable } from '../components/payment'
import { ValidationModal } from '@/components/ui/ValidationModal'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import {
  applicantConfirmDialogProps,
  applicantValidationModalProps,
} from '@/features/applicant/constants/applicantDialogProps'
import { getPaymentConfig, processCardConnectPayment, confirmApplicationFeePayment, processPayPalPayment, confirmRentPayment, confirmDefaultPayment, initializePayment, getApplicantRentAmount } from '@/lib/api/paymentService'
import { getResidentApplicationById } from '@/lib/api/applicationService'
import { listNinjaAccounts, listPlaidAccounts } from '@/lib/api/bankService'
import type { PaymentConfig, PaymentMethod, CreditCardPaymentData, BillingAddress, PaymentRequest as ApiPaymentRequest, PaymentType, PaymentResponse } from '@/types/payment'
import type { ResidentApplicationData, PaymentHistoryItem } from '@/types/application'
import { handleApiError, getUserFriendlyMessage, extractErrorMessage } from '@/lib/utils/errorHandling'
import { APPLICANT_CONTENT } from '@/features/applicant/constants/applicantContent'
import {
  getPaymentType,
  getRemainingAmount,
  shouldShowApplicationFeePurchaseButton,
  shouldShowRentPurchaseButton,
  shouldShowDefaultInvoicePurchaseButton,
  isAccountFrozen,
  isPaymentDisabledByStatus,
} from '@/lib/utils/paymentCalculations'
import { isPaymentAdded, getApplicationStatus } from '@/types/application'
import { formatAmount } from '@/lib/utils/formatters'
import { getApplicantNextStep } from '@/lib/utils/applicationNextSteps'

import { sendAnalytics } from '@/lib/utils/analytics'

/** Matches Angular payment freeze copy (resident-application-add / payment-info-capture). */
const PAYMENT_FROZEN_USER_MESSAGE =
  `You've had too many errors. The account is frozen for 1 hour.\n\nIf you experience problems, contact our Customer Service on 877-581-7368`

function isPaymentFrozenModalMessage(message: string): boolean {
  return message.includes('account is frozen') || message.includes('frozen for 1 hour')
}

// Helper function to strip HTML tags from API error messages and convert to clean text
const stripHtmlTags = (html: string): string => {
  if (!html) return ''
  let text = html
  // Convert <br> and <br/> to newlines
  text = text.replace(/<br\s*\/?>/gi, '\n')
  // Extract text content from <a> tags (keep the visible text, add the href)
  text = text.replace(/<a[^>]*href=["']([^"']*)["'][^>]*>([^<]*)<\/a>/gi, '$2 ($1)')
  // Remove all remaining HTML tags
  text = text.replace(/<[^>]*>/g, '')
  // Decode HTML entities
  text = text.replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&#39;/g, "'")
  // Clean up extra whitespace but preserve newlines
  text = text.split('\n').map(line => line.trim()).join('\n')
  // Remove multiple consecutive newlines
  text = text.replace(/\n{3,}/g, '\n\n')
  return text.trim()
}

function getPaymentErrorModalTitle(message: string): string {
  if (message.includes('Rent Amount has changed')) return 'Rent Amount Changed'
  if (isPaymentFrozenModalMessage(message)) return 'Payment temporarily unavailable'
  return 'Payment Failed'
}

function formatPaymentErrorModalMessage(message: string): string {
  const stripped = stripHtmlTags(message)
    .replace(/Please review your information and try again[\s\S]*/i, '')
    .trim()

  if (message.includes('Your card was declined') || message.includes('Decline') || message.includes('RPCT')) {
    return `Your card was declined with the following error code:\n\n${stripped}`
  }

  const lines = stripped
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)

  let text = lines.length > 0 ? lines.join('\n') : stripped

  if (!message.includes('Rent Amount has changed') && !isPaymentFrozenModalMessage(message)) {
    text +=
      '\n\nPlease review your information and try again. If you are unable to resolve, please contact customer support at help@libertyrent.com'
  }

  return text
}

function extractAuthCodeFromPaymentResponse(response: PaymentResponse): string | undefined {
  const data = response.data
  if (data) {
    const authCode = data.auth_code
    if (typeof authCode === 'string' && authCode) {
      return authCode
    }
    const transactionId = data.transaction_id
    if (typeof transactionId === 'string' && transactionId) {
      return transactionId
    }
  }
  const topLevelAuthCode = response.auth_code
  if (typeof topLevelAuthCode === 'string' && topLevelAuthCode) {
    return topLevelAuthCode
  }
  return undefined
}

function extractPaymentErrorMessage(err: unknown, defaultMessage: string): string {
  if (!err || typeof err !== 'object') {
    return getUserFriendlyMessage(err as Error | string) || defaultMessage
  }

  const apiError = err as {
    message?: string
    response?: {
      data?: string | {
        message?: string
        error?: string
        error_message?: string
        msg?: string
        errors?: Record<string, unknown>
      }
    }
  }

  if (apiError.response?.data) {
    const responseData = apiError.response.data

    if (typeof responseData === 'string') {
      return responseData
    }

    if (typeof responseData === 'object') {
      let errorMessage =
        responseData.message ||
        responseData.error ||
        responseData.error_message ||
        responseData.msg ||
        defaultMessage

      if (responseData.errors && typeof responseData.errors === 'object') {
        const errorMessages = Object.values(responseData.errors).flat()
        if (errorMessages.length > 0) {
          errorMessage = Array.isArray(errorMessages[0])
            ? errorMessages[0].join(', ')
            : String(errorMessages[0])
        }
      }

      return errorMessage
    }
  }

  if (apiError.message) {
    return apiError.message
  }

  return extractErrorMessage(err, defaultMessage)
}

export function PaymentPage() {
  const navigate = useNavigate()
  const { applicantId: urlApplicantId } = useParams<{ applicantId?: string }>()
  const dispatch = useAppDispatch()
  const applicationDataFromRedux = useAppSelector(selectApplicationData)
  const isReadOnly = useAppSelector(selectIsReadOnly)

  // Get applicant ID from URL or Redux
  const applicantId = urlApplicantId || applicationDataFromRedux?.applicant_id

  // Use fresh application data fetched when Purchase is clicked
  const [freshApplicationData, setFreshApplicationData] = useState<ResidentApplicationData | null>(null)
  const [showPaymentForm, setShowPaymentForm] = useState(false)
  const [paymentConfig, setPaymentConfig] = useState<PaymentConfig | null>(null)
  const [isLoadingConfig, setIsLoadingConfig] = useState(false)
  const [isProcessingPayment, setIsProcessingPayment] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [showErrorModal, setShowErrorModal] = useState(false)
  const [errorModalMessage, setErrorModalMessage] = useState<string>('')
  const [paymentCompleted, setPaymentCompleted] = useState(false)
  const [_latestPayment, setLatestPayment] = useState<PaymentHistoryItem | null>(null)
  const [isReturningHome, setIsReturningHome] = useState(false)
  const [currentPaymentType, setCurrentPaymentType] = useState<PaymentType>('APPLICATION_FEE')
  // Payment window / retry countdown (matches Angular pay-application.component)
  const [countdown, setCountdown] = useState<string | null>(null)
  const [isRetryTimerShow, setIsRetryTimerShow] = useState(false)
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  // Track if we started payment-window countdown in handlePurchaseClick (so we don't double-start)
  const paymentWindowCountdownStartedRef = useRef(false)
  // Validation Message modal (matches Angular) when payment window or retry countdown ends
  const [validationModalMessage, setValidationModalMessage] = useState<string | null>(null)
  // Server-side pre-payment validation state (matches Angular's validateApplication)
  const [isValidatingServer, setIsValidatingServer] = useState(false)
  const [serverValidationMessage, setServerValidationMessage] = useState<string | null>(null)
  const serverValidationRedirectTabRef = useRef<ApplicationTab>('signature')
  const serverValidationDoneRef = useRef(false)
  const [validationRedirectTab, setValidationRedirectTab] = useState<'about' | 'income'>('about')

  // Use ref to track if we're in the middle of a payment flow to prevent useEffect from interfering
  const isInPaymentFlowRef = useRef(false)

  // Use fresh data if available, otherwise fall back to Redux state
  const applicationData = freshApplicationData || applicationDataFromRedux

  // Scroll to top when component mounts (when user navigates to this page)
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
  }, [])

  // Server-side pre-payment validation (matches Angular's validateApplication).
  // Fetches fresh data from GET /api/resident_application/{id} and checks 5 required
  // fields before allowing the payment tab to render.
  useEffect(() => {
    if (serverValidationDoneRef.current) return

    const currentApplicantId = urlApplicantId || applicationDataFromRedux?.applicant_id
    if (!currentApplicantId) return

    if (isReadOnly || isPaymentAdded(applicationDataFromRedux)) {
      serverValidationDoneRef.current = true
      return
    }

    serverValidationDoneRef.current = true
    setIsValidatingServer(true)

    getResidentApplicationById(currentApplicantId)
      .then((freshData) => {
        dispatch(setApplicationData(freshData))
        setFreshApplicationData(freshData)

        if (isPaymentAdded(freshData)) return

        const validation = validateApplicationForPayment(freshData)
        if (!validation.isValid) {
          serverValidationRedirectTabRef.current = validation.redirectTab
          setServerValidationMessage(validation.message)
        }
      })
      .catch(() => {
        // If API call fails, don't block — existing error handling will apply
      })
      .finally(() => {
        setIsValidatingServer(false)
      })
  }, [urlApplicantId, applicationDataFromRedux?.applicant_id, isReadOnly, dispatch])

  // Clear countdown interval on unmount
  useEffect(() => {
    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current)
        countdownIntervalRef.current = null
      }
    }
  }, [])

  /**
   * Start countdown timer - timing matches Angular exactly (interval(1000), totalSeconds - secondsPassed - 1).
   * Angular shows (totalSeconds-1) down to 0 over totalSeconds seconds, then completes.
   */
  const startCountdown = (totalSeconds: number, type: 1 | 2) => {
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current)
      countdownIntervalRef.current = null
    }
    let secondsPassed = 0
    const updateMessage = () => {
      const secondsLeft = totalSeconds - secondsPassed - 1 // same as Angular: countdown in reverse
      if (secondsLeft < 0) {
        if (countdownIntervalRef.current) {
          clearInterval(countdownIntervalRef.current)
          countdownIntervalRef.current = null
        }
        setCountdown(null)
        if (type === 2) {
          paymentWindowCountdownStartedRef.current = false
          setShowPaymentForm(false)
          isInPaymentFlowRef.current = false
          setValidationModalMessage('Your payment session has expired due to inactivity. Please try again to continue.')
        } else {
          setIsRetryTimerShow(false)
          setCountdown(null)
          setValidationModalMessage('You can now retry the payment.')
        }
        return
      }
      const minutesLeft = Math.floor(secondsLeft / 60)
      const secs = secondsLeft % 60
      const message =
        minutesLeft > 0
          ? `${minutesLeft} minute and ${secs.toString().padStart(2, '0')} seconds.`
          : `${secs.toString().padStart(2, '0')} seconds.`
      if (type === 2) {
        setCountdown(`Please do not refresh or reload this page. The payment window is ${message}`)
      } else {
        setCountdown(`Please wait or try again after ${message}`)
      }
      secondsPassed += 1
    }
    updateMessage() // first emission (t=0): show totalSeconds-1
    countdownIntervalRef.current = setInterval(updateMessage, 1000)
  }

  // Determine current payment type based on application state
  // Only update if not currently showing payment form (to prevent flickering)
  useEffect(() => {
    if (applicationData && !showPaymentForm) {
      const paymentType = getPaymentType(applicationData)
      setCurrentPaymentType(paymentType)
    }
  }, [applicationData, showPaymentForm])

  // Fetch latest application data to update status dynamically
  // Only check for completed payments when NOT showing payment form and NOT processing payment
  useEffect(() => {
    const fetchLatestStatus = async () => {
      const currentApplicantId = applicationDataFromRedux?.applicant_id
      if (!currentApplicantId) return

      // Don't fetch if user is actively making a payment or in payment flow
      if (showPaymentForm || isProcessingPayment || isInPaymentFlowRef.current) return

      try {
        const latestData = await getResidentApplicationById(currentApplicantId)

        // Update fresh application data with latest info
        setFreshApplicationData(latestData)

        // Update Redux store with latest data
        dispatch(setApplicationData(latestData))

        // Determine what payment type is needed based on application state
        const neededPaymentType = getPaymentType(latestData)

        // Check payment status and remaining balance
        // This ensures purchase button shows if there's still a balance due after partial payment
        if (!showPaymentForm && !isProcessingPayment && !isInPaymentFlowRef.current) {
          // For CONTRACT/DEFAULT_INVOICE, always check remaining balance
          // If there's a remaining balance, ensure paymentCompleted is false so purchase button shows
          if (neededPaymentType === 'CONTRACT' || neededPaymentType === 'DEFAULT_INVOICE') {
            const remainingAmount = getRemainingAmount(latestData)
            if (remainingAmount > 0) {
              // There's still a balance due - ensure purchase button is visible
              setPaymentCompleted(false)
            } else if (latestData.payment_history && latestData.payment_history.length > 0) {
              // No remaining balance - check if there's a completed payment
              const completedPayment = latestData.payment_history.find(
                (payment) => payment.payment_type === neededPaymentType &&
                  (payment.payment_status === 'Completed' || payment.gateway_status === 'Authorized')
              )
              if (completedPayment) {
                setPaymentCompleted(true)
                setLatestPayment(completedPayment)
                setShowPaymentForm(false)
              }
            }
          }
        } else if (!isInPaymentFlowRef.current && !paymentCompleted && latestData.payment_history && latestData.payment_history.length > 0) {
          // For APPLICATION_FEE, check if there's a completed payment
          // For APPLICATION_FEE, check if there's a completed payment specifically for this applicant
          // Skip when in payment flow so a stale fetch doesn't show Thank You after user clicked Purchase (payment lock)
          const completedPayment = latestData.payment_history.find(
            (payment) => {
              const matchesType = payment.payment_type === neededPaymentType;
              const isComp = payment.payment_status === 'Completed' || payment.gateway_status === 'Authorized';
              if (neededPaymentType === 'APPLICATION_FEE') {
                return matchesType && isComp && String(payment.fk_applicant_id) === String(currentApplicantId);
              }
              return matchesType && isComp;
            }
          )
          if (completedPayment) {
            setPaymentCompleted(true)
            setLatestPayment(completedPayment)
            setShowPaymentForm(false)
          }
        }
      } catch {
        // Don't show error to user
      }
    }

    // Fetch on mount and when applicant ID changes, but only if not in payment flow
    if (applicationDataFromRedux?.applicant_id && !showPaymentForm && !isProcessingPayment && !isInPaymentFlowRef.current) {
      fetchLatestStatus()
    }
  }, [applicationDataFromRedux?.applicant_id, dispatch, showPaymentForm, isProcessingPayment, paymentCompleted])

  // Get next step message
  const nextStepMessage = getApplicantNextStep(applicationData?.applicant_status || applicationData?.application_status)

  /**
   * Close the validation modal and redirect the user to the configured tab.
   * Resets the redirect target back to About after navigation.
   */
  const closeValidationModal = () => {
    setValidationModalMessage(null)
    const targetPath = applicantId
      ? getTabPathWithApplicantId(validationRedirectTab, applicantId)
      : `/applicant/application/${validationRedirectTab}`
    if (applicantId) {
      navigate(targetPath, getTabNavigationOptions(validationRedirectTab))
    } else {
      navigate(targetPath)
    }
    setValidationRedirectTab('about')
  }

  const closePaymentErrorModal = () => {
    setShowErrorModal(false)
    setErrorModalMessage('')
  }

  /**
   * After a failed payment attempt, refetch application (Angular payment-info-capture checkFailPayment).
   * If payment_failed_counter >= 5: exit payment form, sync Redux, navigate to /resident-application/:id
   * with payment tab selected (parity with Angular router + fragment #payment).
   */
  const applyPaymentFrozenAfterFailure = useCallback(
    async (currentApplicantId: string): Promise<boolean> => {
      try {
        const data = await getResidentApplicationById(currentApplicantId)
        dispatch(setApplicationData(data))
        setFreshApplicationData(data)
        if (!isAccountFrozen(data)) return false

        paymentWindowCountdownStartedRef.current = false
        if (countdownIntervalRef.current) {
          clearInterval(countdownIntervalRef.current)
          countdownIntervalRef.current = null
        }
        setCountdown(null)
        setIsRetryTimerShow(false)
        setShowPaymentForm(false)
        isInPaymentFlowRef.current = false

        const paymentPath = getTabPathWithApplicantId('payment', currentApplicantId)
        navigate(paymentPath, { ...getTabNavigationOptions('payment'), replace: true })

        setErrorModalMessage(PAYMENT_FROZEN_USER_MESSAGE)
        setShowErrorModal(true)
        return true
      } catch {
        return false
      }
    },
    [dispatch, navigate]
  )

  // Handle server-side validation dismissal — navigate to the tab with missing data
  const handleServerValidationDismiss = () => {
    setServerValidationMessage(null)
    const redirectTab = serverValidationRedirectTabRef.current
    const redirectPath = applicantId
      ? getTabPathWithApplicantId(redirectTab, applicantId)
      : TAB_PATHS[redirectTab]
    if (applicantId) {
      navigate(redirectPath, { ...getTabNavigationOptions(redirectTab), replace: true })
    } else {
      navigate(redirectPath, { replace: true })
    }
  }

  // Handle Purchase button click
  const handlePurchaseClick = async () => {
    const currentApplicantId = applicationDataFromRedux?.applicant_id
    if (!currentApplicantId) {
      setError('Application data not found. Please refresh the page.')
      return
    }

    // Do not allow payment when status is EXPIRED, PENDING, DENIED, CANCELLED (matches Angular)
    if (isPaymentDisabledByStatus(applicationData)) {
      setErrorModalMessage(
        'Payment is not available for this application due to its current status. Your application may have expired, been cancelled, or is under review.'
      )
      setShowErrorModal(true)
      return
    }

    // Set flag to prevent useEffect from interfering
    isInPaymentFlowRef.current = true
    paymentWindowCountdownStartedRef.current = false
    setPaymentCompleted(false) // Reset completed state when starting new payment

    setIsLoadingConfig(true)
    setError(null)
    setSuccessMessage(null)

    try {
      // Fetch latest application data first
      const freshData = await getResidentApplicationById(currentApplicantId)
      setFreshApplicationData(freshData)

      // Check if account is frozen using fresh data (must be after fetch so the latest
      // payment_failed_counter from the server is used, not stale Redux state)
      if (isAccountFrozen(freshData)) {
        dispatch(setApplicationData(freshData))
        const paymentPath = getTabPathWithApplicantId('payment', String(currentApplicantId))
        navigate(paymentPath, { ...getTabNavigationOptions('payment'), replace: true })
        setErrorModalMessage(PAYMENT_FROZEN_USER_MESSAGE)
        setShowErrorModal(true)
        isInPaymentFlowRef.current = false
        setIsLoadingConfig(false)
        return
      }
      /**
       * Fetch linked bank account data required for final-step validation.
       * Treats failed account lookups as empty so validation can still run safely.
       */
      const [ninjaAccounts, plaidAccounts] = await Promise.all([
        listNinjaAccounts(currentApplicantId).catch(() => []),
        listPlaidAccounts(currentApplicantId).catch(() => []),
      ])
      /**
       * Check whether an uploaded file object contains saved file metadata.
       * @returns True when the file has any identifier or path/name metadata.
       */

      const hasValidFileMetadata = (file: {
        attachment_id?: number
        filepath?: string
        filename?: string
        name?: string
      }) => !!(file?.attachment_id || file?.filepath || file?.filename || file?.name)

      /**
     * Determine whether the applicant has uploaded income verification documents.
     * Checks both Ninja income proof and general proof of income file arrays.
     */
      const hasSupportingDocuments =
        (Array.isArray(freshData.ninja_income_proof) &&
          freshData.ninja_income_proof.some(hasValidFileMetadata)) ||
        (Array.isArray(freshData.proof_of_income) &&
          freshData.proof_of_income.some(hasValidFileMetadata))

      /**
     * Determine whether the applicant has a valid linked bank account.
     * Ninja accounts must not be in an initiated or reconnect-required status.
     */
      const hasLinkedBank =
        ninjaAccounts.some(
          (account) =>
            account.ibv_report_status !== 'initiated' &&
            account.ibv_report_status !== 'reconnect_required'
        ) || plaidAccounts.length > 0
      /**
     * Block the final payment flow when income verification is missing.
     * Shows a validation modal and redirects the user to Income and Employment.
     */
      if (!hasSupportingDocuments && !hasLinkedBank) {
        const validationMessage = 'Before submitting your application, please either link your bank or upload income verification documents on the “Income and Employment” tab.'
        isInPaymentFlowRef.current = false
        setShowPaymentForm(false)
        setValidationRedirectTab('income')
        setValidationModalMessage(validationMessage)
        return
      }

      // Update payment type based on fresh data
      const paymentType = getPaymentType(freshData)
      setCurrentPaymentType(paymentType)

      // Initialize payment (POST /api/payment/initialize_payment) - same as Angular; prevents double payment
      const applicationId = freshData.fk_application_id || freshData.application_id
      const applicantIdNum = Number(currentApplicantId)
      const isContractOrDefault = paymentType === 'CONTRACT' || paymentType === 'DEFAULT_INVOICE'
      if (applicationId && applicantIdNum && !isNaN(applicantIdNum)) {
        try {
          const init = await initializePayment(
            Number(applicationId),
            applicantIdNum,
            paymentType
          )
          // Double-payment prevention: when allowPayment is false, show "payment in progress" popup (same as Angular isTimerShow)
          if (!init.allowPayment) {
            const retrySeconds = init.retryPaymentAfter && init.retryPaymentAfter > 0 ? init.retryPaymentAfter : 60
            setIsRetryTimerShow(true)
            setShowPaymentForm(false)
            isInPaymentFlowRef.current = false
            startCountdown(retrySeconds, 1)
            setIsLoadingConfig(false)
            return
          }
          if (init.allowPayment) {
            setIsRetryTimerShow(false)
            setCountdown(null)
            const windowSeconds =
              init.paymentWindow && init.paymentWindow > 0
                ? init.paymentWindow
                : (isContractOrDefault ? 180 : 0)
            if (windowSeconds > 0) {
              paymentWindowCountdownStartedRef.current = true
              startCountdown(windowSeconds, 2)
            }
            // balanceDue can be used to validate/prefill amount if needed
          }
        } catch {
          // If initialize_payment fails, for contract/default still show timer with default window
          if (isContractOrDefault) {
            setIsRetryTimerShow(false)
            setCountdown(null)
            paymentWindowCountdownStartedRef.current = true
            startCountdown(180, 2)
          }
        }
      }

      // Then fetch payment configuration
      const config = await getPaymentConfig()
      setPaymentConfig(config)

      // Always show countdown when payment form is visible: start default 180s if not already started
      if (!paymentWindowCountdownStartedRef.current) {
        startCountdown(180, 2)
      }

      // Show payment form with fresh data
      setShowPaymentForm(true)
    } catch (err: unknown) {
      const errorMessage = extractErrorMessage(err, 'Failed to load payment information. Please try again.')
      setError(errorMessage || 'Failed to load payment information. Please try again.')
      handleApiError(err)
      isInPaymentFlowRef.current = false // Reset flag on error
    } finally {
      setIsLoadingConfig(false)
    }
  }

  // Handle PayPal payment success
  const handlePayPalPaymentSuccess = async (paymentDetails: {
    orderId: string
    paymentId: string // PayPal payment ID (PAYID-...) - this is what backend expects as auth_code
    payerId?: string
    captureId?: string
    transactionId?: string
    status?: string
    paymentAmount?: number // For contract/default_invoice payments
  }) => {
    const { paymentId, paymentAmount } = paymentDetails
    const currentApplicantId = applicationData?.applicant_id || applicationDataFromRedux?.applicant_id

    if (!currentApplicantId) {
      setError('Application data not found. Please refresh the page.')
      return
    }

    setIsProcessingPayment(true)
    setError(null)
    setSuccessMessage(null)

    try {
      // Fetch latest application data before processing payment to ensure we have the most up-to-date information
      const latestApplicationData = await getResidentApplicationById(currentApplicantId)
      setFreshApplicationData(latestApplicationData)
      dispatch(setApplicationData(latestApplicationData))

      // Determine payment type from fresh data
      const paymentType = getPaymentType(latestApplicationData)
      setCurrentPaymentType(paymentType)

      // Validate payment amount based on type using fresh data
      if (paymentType === 'APPLICATION_FEE') {
        if (latestApplicationData.application_fee === undefined || latestApplicationData.application_fee === null) {
          setError('Application fee is not available. Please refresh the page or contact support.')
          setIsProcessingPayment(false)
          return
        }
      } else if (paymentType === 'CONTRACT' || paymentType === 'DEFAULT_INVOICE') {
        const remainingAmount = getRemainingAmount(latestApplicationData)
        if (remainingAmount <= 0) {
          setError('There is no due amount to pay.')
          setIsProcessingPayment(false)
          return
        }
      }

      // Get tabid from session storage
      let tabid = sessionStorage.getItem('tabId')

      if (!tabid) {
        tabid = `${Date.now()}_${Math.random().toString(36).substring(2, 11)}`
        sessionStorage.setItem('tabId', tabid)
      }

      if (tabid.startsWith('tab_')) {
        tabid = tabid.substring(4)
      }

      // Determine payment amount to send using fresh data
      const amountToSend = paymentAmount ||
        (paymentType === 'APPLICATION_FEE'
          ? Number(latestApplicationData.application_fee)
          : getRemainingAmount(latestApplicationData))

      // Process payment based on type
      let response: PaymentResponse

      if (paymentType === 'APPLICATION_FEE') {
        // Application fee payment
        const paymentRequest: Partial<ApiPaymentRequest> = {
          payment_source: 'PAYPAL',
          payment_data: {
            auth_code: paymentId,
          },
          tabid: tabid,
        }
        response = await processPayPalPayment(currentApplicantId, paymentRequest)
      } else if (paymentType === 'CONTRACT') {
        // Contract payment - use rent_payment endpoint
        response = await confirmRentPayment(
          currentApplicantId,
          'PAYPAL',
          paymentId,
          amountToSend > 0 ? String(amountToSend) : undefined,
          tabid
        )
      } else if (paymentType === 'DEFAULT_INVOICE') {
        // Default invoice payment
        response = await confirmDefaultPayment(
          currentApplicantId,
          'PAYPAL',
          paymentId,
          amountToSend > 0 ? String(amountToSend) : undefined,
          tabid
        )
      } else {
        throw new Error('Invalid payment type')
      }

      if (response.success) {
        const successMessage = paymentType === 'APPLICATION_FEE'
          ? 'Application fee payment processed successfully!'
          : paymentType === 'CONTRACT'
            ? 'Rent amount paid successfully!'
            : 'Payment processed successfully!'
        setSuccessMessage(successMessage)
        if (paymentType === 'APPLICATION_FEE') {
          sendAnalytics('click', 'resident pay Application Fee', '')
        } else if (paymentType === 'CONTRACT') {
          sendAnalytics('click', 'resident pay Contract', '')
        } else if (paymentType === 'DEFAULT_INVOICE') {
          sendAnalytics('click', 'resident pay Default', '')
        }

        // Clear payment window countdown on success
        paymentWindowCountdownStartedRef.current = false
        if (countdownIntervalRef.current) {
          clearInterval(countdownIntervalRef.current)
          countdownIntervalRef.current = null
        }
        setCountdown(null)

        // Immediately show payment log
        // Don't wait for API response - show the log right away
        setShowPaymentForm(false)
        isInPaymentFlowRef.current = false

        // For CONTRACT/DEFAULT_INVOICE payments, check if there's still a remaining balance
        // Only set paymentCompleted to true if there's no remaining balance
        if (paymentType === 'CONTRACT' || paymentType === 'DEFAULT_INVOICE') {
          // Fetch latest data to check remaining balance
          try {
            if (currentApplicantId) {
              const updatedData = await getResidentApplicationById(currentApplicantId)
              const remainingAmount = getRemainingAmount(updatedData)
              // Only mark as completed if there's no remaining balance
              setPaymentCompleted(remainingAmount <= 0)
            } else {
              // If we can't check, assume not completed (will be checked in fetchLatestStatus)
              setPaymentCompleted(false)
            }
          } catch {
            // If fetch fails, assume not completed (will be checked in fetchLatestStatus)
            setPaymentCompleted(false)
          }
        } else {
          // For APPLICATION_FEE, payment is completed
          setPaymentCompleted(true)
        }

        // Scroll to top to show payment log immediately
        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' })
        }, 100)

        // Fetch latest application data to get updated status and payment details in background
        try {
          if (currentApplicantId) {
            const updatedData = await getResidentApplicationById(currentApplicantId)

            setFreshApplicationData(updatedData)
            dispatch(setApplicationData(updatedData))

            // Get the latest payment from payment_history
            if (updatedData.payment_history && updatedData.payment_history.length > 0) {
              const sortedPayments = [...updatedData.payment_history].sort((a, b) => {
                const timeA = Number(a.created_at) || 0
                const timeB = Number(b.created_at) || 0
                return timeB - timeA
              })
              setLatestPayment(sortedPayments[0])
            } else {
              setLatestPayment(null)
            }

            // Update paymentCompleted based on remaining balance for CONTRACT/DEFAULT_INVOICE
            if (paymentType === 'CONTRACT' || paymentType === 'DEFAULT_INVOICE') {
              const remainingAmount = getRemainingAmount(updatedData)
              setPaymentCompleted(remainingAmount <= 0)
            }
          }
        } catch {
          // Even if fetch fails, check paymentCompleted state based on payment type
          setLatestPayment(null)
          // For CONTRACT/DEFAULT_INVOICE, assume not completed if we can't check
          if (paymentType === 'CONTRACT' || paymentType === 'DEFAULT_INVOICE') {
            setPaymentCompleted(false)
          }
        }
      } else {
        const errorMsg = response.message || 'PayPal payment processing failed. Please try again.'
        const frozen = await applyPaymentFrozenAfterFailure(String(currentApplicantId))
        if (!frozen) {
          setErrorModalMessage(errorMsg)
          setShowErrorModal(true)
          setError(errorMsg)
        }
      }
    } catch (err: unknown) {
      const errorMessage = extractPaymentErrorMessage(
        err,
        'PayPal payment processing failed. Please try again.'
      )

      const frozen = currentApplicantId
        ? await applyPaymentFrozenAfterFailure(String(currentApplicantId))
        : false
      if (!frozen) {
        setErrorModalMessage(errorMessage)
        setShowErrorModal(true)
        setError(errorMessage)
        handleApiError(err)
      }
    } finally {
      setIsProcessingPayment(false)
    }
  }

  // Handle PayPal payment error
  const handlePayPalPaymentError = (error: string) => {
    setError(error)
    setIsProcessingPayment(false)
    const id = applicationData?.applicant_id || applicationDataFromRedux?.applicant_id
    if (id) {
      void applyPaymentFrozenAfterFailure(String(id)).then((frozen) => {
        if (!frozen) {
          setErrorModalMessage(error)
          setShowErrorModal(true)
        }
      })
    } else {
      setErrorModalMessage(error)
      setShowErrorModal(true)
    }
  }

  // Handle payment form submission
  const handlePaymentSubmit = async (
    paymentData: CreditCardPaymentData,
    paymentMethod: PaymentMethod,
    paymentAmountOverride?: number // For contract/default_invoice payments
  ) => {
    const currentApplicantId = applicationData?.applicant_id || applicationDataFromRedux?.applicant_id

    if (!currentApplicantId) {
      setError('Application data not found. Please refresh the page.')
      return
    }

    setIsProcessingPayment(true)
    setError(null)
    setSuccessMessage(null)

    try {
      // Fetch latest application data before processing payment to ensure we have the most up-to-date information
      const latestApplicationData = await getResidentApplicationById(currentApplicantId)
      setFreshApplicationData(latestApplicationData)
      dispatch(setApplicationData(latestApplicationData))

      // Determine payment type from fresh data
      const paymentType = getPaymentType(latestApplicationData)
      setCurrentPaymentType(paymentType)

      // Validate payment amount based on type using fresh data
      if (paymentType === 'APPLICATION_FEE') {
        if (latestApplicationData.application_fee === undefined || latestApplicationData.application_fee === null) {
          setError('Application fee is not available. Please refresh the page or contact support.')
          setIsProcessingPayment(false)
          return
        }
      } else if (paymentType === 'CONTRACT' || paymentType === 'DEFAULT_INVOICE') {
        const remainingAmount = getRemainingAmount(latestApplicationData)
        if (remainingAmount <= 0) {
          setError('There is no due amount to pay.')
          setIsProcessingPayment(false)
          return
        }

        // For CONTRACT payments, validate rent amount hasn't changed (per documentation)
        if (paymentType === 'CONTRACT') {
          try {
            const storedRentAmount = latestApplicationData.rent_amount

            // Only validate if stored rent amount exists
            if (storedRentAmount !== null && storedRentAmount !== undefined) {
              const currentRentAmount = await getApplicantRentAmount(currentApplicantId)

              // Only validate if both values exist and are valid
              if (currentRentAmount !== null && currentRentAmount !== undefined) {
                const storedAmount = Number(storedRentAmount)
                const currentAmount = Number(currentRentAmount)

                // Check if rent amount has changed (with small tolerance for floating point differences)
                // Only validate if both amounts are valid numbers greater than 0
                if (!isNaN(storedAmount) && !isNaN(currentAmount) && storedAmount > 0 && currentAmount > 0) {
                  const difference = Math.abs(storedAmount - currentAmount)
                  const hasChanged = difference > 0.01 // Allow 1 cent difference for floating point precision

                  if (hasChanged) {
                    setIsProcessingPayment(false)
                    // Show error in popup modal with refresh option
                    setErrorModalMessage('Rent Amount has changed. Please refresh to show latest rent amount before adding payment.')
                    setShowErrorModal(true)
                    return
                  }
                }
              }
            }
          } catch {
            // If rent amount validation fails, silently continue
            // This is a safety check, not a hard requirement
            // Don't block payment if validation fails
          }
        }
      }

      // Get tabid from session storage
      const tabid = sessionStorage.getItem('tabId') || undefined

      // Normalize address_type: API expects 'POBOX' not 'PO_BOX'
      const normalizedAddressType = paymentData.billing_address.address_type === 'PO_BOX'
        ? 'POBOX'
        : (paymentData.billing_address.address_type || 'STREET')

      // Get ra_id from current_address if available (using fresh data)
      const raId = latestApplicationData.current_address?.ra_id || null

      // Build billing address matching production API format
      const billingAddressRequest: BillingAddress = {
        house_no: paymentData.billing_address.house_no,
        street: paymentData.billing_address.street,
        street_type: '', // API requires this field, but we don't use it in UI
        city: paymentData.billing_address.city,
        state: paymentData.billing_address.state || '',
        zip_code: paymentData.billing_address.zip_code,
        address_type: normalizedAddressType,
        apartment: paymentData.billing_address.apartment || null,
        // Production API format fields
        ispobox: normalizedAddressType === 'POBOX',
        pobox_number: normalizedAddressType === 'POBOX' ? '' : '',
        payment_address: '',
        ra_id: raId,
        address_data: {},
      }

      // Determine payment amount using fresh data
      const amountToSend = paymentAmountOverride ||
        (paymentType === 'APPLICATION_FEE'
          ? Number(latestApplicationData.application_fee)
          : getRemainingAmount(latestApplicationData))

      // Build payment request matching production API format
      // For CONTRACT/DEFAULT_INVOICE, include payment_amount
      const paymentRequest: ApiPaymentRequest = {
        first_name: paymentData.first_name,
        last_name: paymentData.last_name,
        card_number: paymentData.card_number.trim(),
        expiry_month: paymentData.expiry_month,
        expiry_year: paymentData.expiry_year,
        cvv: paymentData.cvv,
        billing_address: billingAddressRequest,
        applicant_id: String(currentApplicantId),
        payment_type: paymentType,
        ...(paymentType !== 'APPLICATION_FEE' && amountToSend > 0 && { payment_amount: String(amountToSend) }),
        ...(tabid && { tabid }),
      }

      // Process payment
      const response = await processCardConnectPayment(paymentRequest)

        if (response.success) {
        const successMessage = paymentType === 'APPLICATION_FEE'
          ? 'Application fee payment processed successfully!'
          : paymentType === 'CONTRACT'
            ? 'Rent amount paid successfully!'
            : 'Payment processed successfully!'
        setSuccessMessage(successMessage)
        if (paymentType === 'APPLICATION_FEE') {
          sendAnalytics('click', 'resident pay Application Fee', '')
        } else if (paymentType === 'CONTRACT') {
          sendAnalytics('click', 'resident pay Contract', '')
        } else if (paymentType === 'DEFAULT_INVOICE') {
          sendAnalytics('click', 'resident pay Default', '')
        }

        // Clear payment window countdown on success
        paymentWindowCountdownStartedRef.current = false
        if (countdownIntervalRef.current) {
          clearInterval(countdownIntervalRef.current)
          countdownIntervalRef.current = null
        }
        setCountdown(null)

        // Extract auth_code from payment response
        const authCode = extractAuthCodeFromPaymentResponse(response)

        // Call appropriate confirmation endpoint based on payment type
        if (authCode) {
          try {
            const tabid = sessionStorage.getItem('tabId') || undefined

            if (paymentType === 'APPLICATION_FEE') {
              await confirmApplicationFeePayment(
                currentApplicantId,
                paymentMethod,
                authCode,
                tabid
              )
            } else if (paymentType === 'DEFAULT_INVOICE') {
              await confirmDefaultPayment(
                currentApplicantId,
                paymentMethod,
                authCode,
                amountToSend > 0 ? String(amountToSend) : undefined,
                tabid
              )
            }
          } catch {
            // Error occurred but don't fail the payment - payment was already successful
          }
        }

        // Fetch latest application data to get updated status and payment details
        try {
          if (currentApplicantId) {
            const updatedData = await getResidentApplicationById(currentApplicantId)

            setFreshApplicationData(updatedData)
            dispatch(setApplicationData(updatedData))

            // Get the latest payment from payment_history
            if (updatedData.payment_history && updatedData.payment_history.length > 0) {
              const sortedPayments = [...updatedData.payment_history].sort((a, b) => {
                const timeA = Number(a.created_at) || 0
                const timeB = Number(b.created_at) || 0
                return timeB - timeA
              })
              setLatestPayment(sortedPayments[0])
            } else {
              // Even if no payment history yet, set to null so payment log section shows
              setLatestPayment(null)
            }

            // Hide payment form and show payment log - stay on payment page
            setShowPaymentForm(false)
            isInPaymentFlowRef.current = false // Reset flag after payment completes

            // For CONTRACT/DEFAULT_INVOICE payments, check if there's still a remaining balance
            // Only set paymentCompleted to true if there's no remaining balance
            if (paymentType === 'CONTRACT' || paymentType === 'DEFAULT_INVOICE') {
              const remainingAmount = getRemainingAmount(updatedData)
              // Only mark as completed if there's no remaining balance
              setPaymentCompleted(remainingAmount <= 0)
            } else {
              // For APPLICATION_FEE, payment is completed
              setPaymentCompleted(true)
            }

            // Scroll to top to show payment log immediately
            setTimeout(() => {
              window.scrollTo({ top: 0, behavior: 'smooth' })
            }, 100)
          }
        } catch {
          // Even on error, show payment completed state so user can see payment log
          setPaymentCompleted(true)
          setLatestPayment(null)
          isInPaymentFlowRef.current = false // Reset flag even on error
        }
      } else {
        // Show error in popup modal (after failure, check freeze — Angular payment-info-capture)
        const errorMsg = response.message || 'Payment processing failed. Please try again.'
        const frozen = await applyPaymentFrozenAfterFailure(String(currentApplicantId))
        if (!frozen) {
          setErrorModalMessage(errorMsg)
          setShowErrorModal(true)
          setError(errorMsg)
        }
      }
    } catch (err: unknown) {
      const errorMessage = extractPaymentErrorMessage(
        err,
        'Payment processing failed. Please try again.'
      )

      const frozen = await applyPaymentFrozenAfterFailure(String(currentApplicantId))
      if (!frozen) {
        setErrorModalMessage(errorMessage)
        setShowErrorModal(true)
        setError(errorMessage)
        handleApiError(err)
      }
    } finally {
      setIsProcessingPayment(false)
    }
  }

  // Handle Return To Home button click
  const handleReturnToHome = async () => {
    const currentApplicantId = applicationData?.applicant_id
    if (!currentApplicantId) {
      setError('Application ID not found. Please refresh the page.')
      return
    }

    setIsReturningHome(true)
    setError(null)

    try {
      // Make first API call: GET /api/resident_application (fetch all applications)
      await dispatch(fetchApplications()).unwrap()

      // Make second API call: GET /api/resident_application/{applicant_id}
      await dispatch(fetchApplicationById(currentApplicantId)).unwrap()

      // Navigate to About page
      const aboutPath = applicantId
        ? getTabPathWithApplicantId('about', applicantId)
        : '/applicant/application/about'
      if (applicantId) {
        navigate(aboutPath, getTabNavigationOptions('about'))
      } else {
        navigate(aboutPath)
      }
    } catch (err: unknown) {
      const errorMessage = extractErrorMessage(err, 'Failed to load application data. Please try again.')
      setError(errorMessage || 'Failed to load application data. Please try again.')
      handleApiError(err)
    } finally {
      setIsReturningHome(false)
    }
  }

  // Get application fee from API - use actual value only (no fallback)
  // API returns application_fee as string or number
  const applicationFee = applicationData?.application_fee !== undefined && applicationData?.application_fee !== null
    ? Number(applicationData.application_fee)
    : null

  // Show silent loading spinner during server-side pre-payment validation (below header + tabs)
  if (isValidatingServer) {
    return (
      <div>
        <ApplicationHeader
          {...getApplicationHeaderProps(applicationData || applicationDataFromRedux, APPLICANT_CONTENT.application.status.incomplete)}
        />
        <ApplicantTabNavigation />
        <div className="p-3 sm:p-6 bg-gray-50 min-h-screen">
          <div className="flex justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        </div>
      </div>
    )
  }

  // Show loading state if application data is not yet loaded
  if (!applicationData) {
    return (
      <div className="p-3 sm:p-6 bg-gray-50 min-h-screen">
        <div className="bg-white rounded-xl shadow-md border border-gray-200 p-4 sm:p-8">
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
            <p className="text-gray-600 text-lg">{APPLICANT_CONTENT.application.messages.loading}</p>
            <p className="text-gray-500 text-sm mt-2">Please wait while we load your payment information.</p>
          </div>
        </div>
      </div>
    )
  }

  // Show error if application fee is not available
  if (applicationFee === null) {
    return (
      <div className="p-3 sm:p-6 bg-gray-50 min-h-screen">
        <div className="bg-white rounded-xl shadow-md border border-gray-200 p-4 sm:p-8">
          <div className="text-center py-12">
            <AlertCircle className="w-12 h-12 mx-auto mb-4 text-red-600" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Application Fee Not Available</h2>
            <p className="text-gray-600 mb-4">
              The application fee information is not available. Please refresh the page or contact support.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-2 bg-[#1597ff] text-white font-medium rounded-lg hover:bg-[#0d7acc] transition-colors"
            >
              Refresh Page
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Server-side validation failed — show error modal listing missing fields and redirect on dismiss
  if (serverValidationMessage) {
    return (
      <div>
        <ApplicationHeader
          {...getApplicationHeaderProps(applicationData, APPLICANT_CONTENT.application.status.incomplete)}
        />
        <ApplicantTabNavigation />
        <div className="p-3 sm:p-6 bg-gray-50 min-h-screen">
          <ValidationModal
            {...applicantValidationModalProps}
            isOpen={true}
            title={APPLICANT_CONTENT.errors.validationMessage}
            message={serverValidationMessage}
            onClose={handleServerValidationDismiss}
          />
        </div>
      </div>
    )
  }

  return (
    <div>
      {/* Header - Resident Application */}
      <ApplicationHeader
        {...getApplicationHeaderProps(applicationData, APPLICANT_CONTENT.application.status.incomplete)}
      />

      {/* Tab Navigation */}
      <ApplicantTabNavigation />

      {/* Content - form-readonly when applicant_lock so header/stepper stay clickable (per Angular) */}
      <div className={applicationData?.applicant_lock === "1" ? "form-readonly" : undefined}>
      <div className="p-3 sm:p-6 bg-gray-50 min-h-screen">

        {/* Payment in progress popup - matches Angular payment-window + alert-ylw (lock, message, countdown, BACK) */}
        {isRetryTimerShow && (
          <div className="flex flex-col items-center justify-center min-h-[70vh] w-full">
            <div className="bg-[#fef9ec] border border-[#faebcc] rounded-none shadow-none p-8 max-w-lg w-full text-center" role="alert">
              <Lock className="w-14 h-14 text-red-600 mx-auto mb-4 flex-shrink-0" aria-hidden />
              <h2 className="text-[#8a6d3b] font-bold text-xl mb-3">Payment for this application is in progress.</h2>
              <p className="text-[#8a6d3b] text-base mb-6 min-h-[1.5rem]">
                {countdown || 'Please wait or try again after...'}
              </p>
              <button
                type="button"
                onClick={handleReturnToHome}
                disabled={isReturningHome}
                className="px-8 py-3 bg-[#1597ff] text-white text-[14px] font-medium rounded hover:bg-[#0d7acc] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isReturningHome ? 'Loading...' : 'BACK'}
              </button>
            </div>
          </div>
        )}

        {/* Payment Completed - Show Angular-matching alert */}
        {paymentCompleted && (
          <div className="space-y-0 mb-6">
            {/* Thank You message matching Angular alert-ylw */}
            <div className="bg-[#fef9ec] p-[10px] text-[14px] text-[#94845b] font-normal mt-5">
              <b>
                {currentPaymentType === 'APPLICATION_FEE'
                  ? 'Thank You for Your Application Payment!!!'
                  : currentPaymentType === 'CONTRACT'
                    ? 'Thank You for Your Rent Payment!!!'
                    : 'Thank You for Your Payment!!!'}
              </b>
              <p className="mt-1">
                {currentPaymentType === 'APPLICATION_FEE'
                  ? 'We have received your application. Our underwriters will review your application shortly. We\'ll send you an email letting you know how to proceed once a decision has been made.'
                  : 'Your payment has been processed successfully.'}
              </p>
            </div>

            {/* Payment Log - Angular style: inside alert-ylw div, no white card wrapper */}
            <PaymentLogTable
              paymentHistory={applicationData.payment_history || []}
              paymentAdjustments={applicationData.payment_adjustment}
              paymentAndPaymentAdjustment={applicationData.payment_and_payment_adjustment}
              applicantId={applicantId ? String(applicantId) : undefined}
            />

            {/* Return To Home button - matching Angular */}
            <div className="mt-4">
              <button
                onClick={handleReturnToHome}
                disabled={isReturningHome}
                className="inline-flex items-center gap-2 px-8 py-3 bg-white text-[#1597ff] border-2 border-[#1597ff] font-semibold rounded-lg hover:bg-blue-50 transition-colors shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isReturningHome ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading...
                  </>
                ) : (
                  'Return To Home'
                )}
              </button>
            </div>
          </div>
        )}

        {!isRetryTimerShow && !showPaymentForm && !paymentCompleted && (() => {
          // Determine which purchase button scenario to show
          const appFeeButton = shouldShowApplicationFeePurchaseButton(applicationData)
          const rentButton = shouldShowRentPurchaseButton(applicationData)
          const defaultInvoiceButton = shouldShowDefaultInvoicePurchaseButton(applicationData)
          const remainingAmount = getRemainingAmount(applicationData)

          // Show application fee payment scenario
          if (appFeeButton && currentPaymentType === 'APPLICATION_FEE') {
            return (
              <div className="bg-[#fff8e1] rounded-none shadow-none border border-[#faebcc] p-6 mb-6">

                {/* Information Box Content */}
                <div className="mb-4">
                  <h3 className="text-[#8a6d3b] font-bold text-[15px] mb-1">You are almost done.</h3>
                  <div className="space-y-3 text-[#8a6d3b] text-[13px] leading-5">
                    <p>
                      {applicationData?.application_fee_invoice &&
                        (applicationData.application_fee_invoice === true ||
                          applicationData.application_fee_invoice === '1' ||
                          applicationData.application_fee_invoice === 1)
                        ? `Your balance due is $${applicationFee !== null ? applicationFee.toFixed(2) : 'N/A'}. Please click the Purchase button to review your payment options.`
                        : `Click the Purchase button to pay our $${applicationFee !== null ? applicationFee.toFixed(2) : 'N/A'} application processing fee to continue. You will be prompted to select your preferred payment method.`
                      }
                    </p>
                    <p>
                      We will review your application and give you an answer shortly. All payments must be made using cards or payment accounts using Applicant's name.
                    </p>
                  </div>
                </div>

                {error && (
                  <div className="mb-4 p-3 bg-red-50 border-l-4 border-red-400 rounded-sm">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-red-800">{error}</p>
                    </div>
                  </div>
                )}

                <div className="flex flex-wrap items-center gap-4">
                  <button
                    onClick={handlePurchaseClick}
                    disabled={isLoadingConfig || applicationFee === null || !appFeeButton}
                    className="px-6 py-2 bg-[#0091ff] text-white text-[14px] font-medium rounded hover:bg-[#007acc] transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {isLoadingConfig && <Loader2 className="w-3 h-3 animate-spin" />}
                    Purchase
                  </button>
                  <p className="text-[12px] font-bold text-[#8a6d3b] ml-2">
                    Note: The application fee is non-refundable, unless Liberty Rent denies your application.
                  </p>
                </div>
              </div>
            )
          }

          // Show rent payment scenario (Angular parity: first-time contract vs rent increase / account adjusted)
          if (rentButton.show && currentPaymentType === 'CONTRACT') {
            const hasRentAdded = applicationData?.rent_added === true || applicationData?.rent_added === '1' || applicationData?.rent_added === 1
            const balanceDueForRent = applicationData?.balance_due ? Number(applicationData.balance_due) : 0
            const isRentIncreaseScenario = hasRentAdded && balanceDueForRent > 0
            return (
              <>
                <div className="bg-[#fff8e1] rounded-none shadow-none border border-[#faebcc] p-6 mb-6">
                  <div className="mb-4">
                    <h3 className="text-[#8a6d3b] font-bold text-[15px] mb-1">
                      {isRentIncreaseScenario ? 'Thank you for your business. Your account has been adjusted.' : 'You have been Approved.'}
                    </h3>
                    {!rentButton.allRequired && (
                      <p className="text-[#8a6d3b] text-[13px] leading-5">
                        {isRentIncreaseScenario ? (
                          <>
                            Your Access Fee is being increased due to an update from your property regarding the new Rent Amount for {formatAmount(Number(applicationData?.rent_amount || 0))}. You owe: {formatAmount(remainingAmount)}.
                            <br />
                            This must be paid prior to your move in date.
                            <br />
                            Please click the Purchase button to review your payment options.
                          </>
                        ) : (
                          <>Click the Purchase button to buy your contract now. All payments must be made using cards or payment accounts using Applicant's name. Your balance due is ${remainingAmount.toFixed(2)}.</>
                        )}
                      </p>
                    )}
                  </div>

                  {error && (
                    <div className="mb-4 p-3 bg-red-50 border-l-4 border-red-400 rounded-sm">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                        <p className="text-xs text-red-800">{error}</p>
                      </div>
                    </div>
                  )}

                  {!rentButton.allRequired ? (
                    <div className="flex flex-wrap items-center gap-4">
                      <button
                        onClick={handlePurchaseClick}
                        disabled={(isReadOnly && getApplicationStatus(applicationData) !== 'APPROVED') || isLoadingConfig || rentButton.disabled || remainingAmount <= 0}
                        className="px-6 py-2 bg-[#0091ff] text-white text-[14px] font-medium rounded hover:bg-[#007acc] transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                      >
                        {isLoadingConfig && <Loader2 className="w-3 h-3 animate-spin" />}
                        Purchase
                      </button>
                      <p className="text-[12px] font-bold text-[#8a6d3b] ml-2">
                        Note: The access fee is non-refundable, unless you do not move forward with the Lease. The Liberty Rent Certificate is binding for the Lease.
                      </p>
                    </div>
                  ) : (
                    <p className="text-[#8a6d3b] text-[13px] leading-5 mt-3 whitespace-pre-line">
                      {rentButton.allRequiredMessage}
                    </p>
                  )}
                </div>

              </>
            )
          }

          // Show default invoice scenario (reimbursement repayment — hide rent-increase copy)
          if (defaultInvoiceButton.show && currentPaymentType === 'DEFAULT_INVOICE') {
            return (
              <div className="bg-[#fff8e1] rounded-none shadow-none border border-[#faebcc] p-6 mb-6">
                <div className="mb-4">
                  <h3 className="text-[#8a6d3b] font-bold text-[15px] mb-1">
                    Thank you for your business. Your account has been adjusted.
                  </h3>
                </div>

                {error && (
                  <div className="mb-4 p-3 bg-red-50 border-l-4 border-red-400 rounded-sm">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-red-800">{error}</p>
                    </div>
                  </div>
                )}

                {!defaultInvoiceButton.allRequired ? (
                  <div className="flex flex-wrap items-center gap-4">
                    <button
                      onClick={handlePurchaseClick}
                      disabled={(isReadOnly && getApplicationStatus(applicationData) !== 'APPROVED') || isLoadingConfig || defaultInvoiceButton.disabled || remainingAmount <= 0}
                      className="px-6 py-2 bg-[#0091ff] text-white text-[14px] font-medium rounded hover:bg-[#007acc] transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {isLoadingConfig && <Loader2 className="w-3 h-3 animate-spin" />}
                      Purchase
                    </button>
                    <p className="text-[12px] font-bold text-[#8a6d3b] ml-2">
                      Note: This invoice is to repay Liberty Rent for reimbursements paid to a property on a prior Liberty Rent approved lease agreement. In order to reuse Liberty Rent services, this invoice must be repaid in full, in a one-time payment. Please note that repayment does not guarantee application approval.
                    </p>
                  </div>
                ) : (
                  <p className="text-[#8a6d3b] text-[13px] leading-5 mt-3 whitespace-pre-line">
                    {defaultInvoiceButton.allRequiredMessage}
                  </p>
                )}
              </div>
            )
          }

          // No purchase button scenario - show status-specific messages matching Angular
          const paymentAdded = isPaymentAdded(applicationData)
          const status = getApplicationStatus(applicationData)
          const normalizedStatus = status.toUpperCase()
          const rentAdded = applicationData?.rent_added
          const hasRentAdded = rentAdded === true || (typeof rentAdded === 'string' && rentAdded === '1') || (typeof rentAdded === 'number' && rentAdded === 1)
          const hasApplicationFeeInvoice = !!applicationData?.application_fee_invoice &&
            (applicationData.application_fee_invoice === true ||
              applicationData.application_fee_invoice === '1' ||
              applicationData.application_fee_invoice === 1)
          const balanceDue = applicationData?.balance_due ? Number(applicationData.balance_due) : 0
          const isCancelledStatus = normalizedStatus === 'CANCELLED' || applicationData?.application_status === 'CANCELLED'

          // Scenario 0: Approved Primary but Pending Co-Applicants
          const coResidents = applicationData.co_resident || []
          const coResidentApps = applicationData.co_resident_application || []
          const allCoResidents = [...coResidents, ...coResidentApps]
          const areAllCoApplicantsApproved = allCoResidents.length === 0 || allCoResidents.every(APP => {
            const status = APP.applicant_status ? String(APP.applicant_status).toUpperCase() : ''
            if (['CANCELLED', 'VOID', 'DENIED'].includes(status)) return true
            return status === 'APPROVED' || status === 'PROPERTY_APPROVED'
          })

          return (
            <div className="space-y-0">
              {normalizedStatus === 'APPROVED' && !areAllCoApplicantsApproved && (
                <div className="bg-[#fef9ec] p-[10px] text-[14px] text-[#94845b] font-normal mt-5">
                  <b>Thank you for your business. Your account has been adjusted.</b>
                  <p className="mt-1">Please complete Co-Applicant Details to proceed.</p>
                </div>
              )}

              {/* Scenario 1: Pending - payment added, not approved/cancelled, no fee invoice */}
              {paymentAdded && normalizedStatus !== 'APPROVED' && !isCancelledStatus && !hasApplicationFeeInvoice && (
                <div className="bg-[#fef9ec] p-[10px] text-[14px] text-[#94845b] font-normal mt-5">
                  <b>Thank You for Your Application Payment !!!</b>
                  <p className="mt-1">
                  We have received your application. Our underwriters will review your application shortly.
                  We'll send you an email letting you know how to proceed once a decision has been made.
                  </p>
                </div>
              )}

              {/* PENDING status - next step message */}
              {normalizedStatus === 'PENDING' && (
                <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-5">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <AlertCircle className="h-5 w-5 text-blue-400" aria-hidden="true" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-blue-700">
                        {nextStepMessage || 'Liberty Rent is reviewing your Application and will send an update shortly'}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* DENIED status - show denial message */}
              {normalizedStatus === 'DENIED' && (
                <div className="bg-[#fef9ec] p-[10px] text-[14px] text-[#94845b] font-normal mt-5">
                  <b>Application Denied</b>
                  <p className="mt-1">We are sorry but you did not meet our minimum requirements.</p>
                </div>
              )}

              {/* EXPIRED status - show expired message (matches Angular: no purchase, contact to unexpire) */}
              {normalizedStatus === 'EXPIRED' && (
                <div className="bg-[#fef9ec] p-[10px] text-[14px] text-[#94845b] font-normal mt-5">
                  <b>Application Expired</b>
                  <p className="mt-1">
                    Your application has been expired, due to inactivity.
                    If your application is less than 30 days old, you can contact Liberty Customer Service to unexpire it if you want to continue to complete your application.
                  </p>
                  <p className="mt-2">
                    Contact{' '}
                    <a href="mailto:help@libertyrent.com" className="text-[#1597ff] underline">
                      help@libertyrent.com
                    </a>{' '}
                    or (877) 581-7368.
                  </p>
                </div>
              )}

              {/* CANCELLED status - always show cancelled message (matches Angular: hidden purchase + cancelled alert) */}
              {isCancelledStatus && (
                <div className="bg-[#fef9ec] p-[10px] text-[14px] text-[#94845b] font-normal mt-5">
                  <b>Thank you for your business. Your account has been adjusted.</b>
                  <p className="mt-1">Your application has been cancelled.</p>
                </div>
              )}

              {/* What's Next Message - Display for other statuses not covered by specific alerts above */}
              {nextStepMessage && !['PENDING', 'DENIED', 'EXPIRED', 'CANCELLED'].includes(normalizedStatus) && !isCancelledStatus && !paymentCompleted && (
                <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-5">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <AlertCircle className="h-5 w-5 text-blue-400" aria-hidden="true" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-blue-700">
                        <span className="font-bold">What's Next: </span>
                        {nextStepMessage}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Scenario 2: Application fee invoice, not cancelled */}
              {hasApplicationFeeInvoice && !isCancelledStatus && (
                <div className="bg-[#fef9ec] p-[10px] text-[14px] text-[#94845b] font-normal mt-5">
                  <b>Thank you for your business. Your account has been adjusted.</b>
                  <p className="mt-1">Your balance due is ${applicationFee !== null ? applicationFee.toFixed(2) : 'N/A'}. Please click the Purchase button to review your payment options.</p>
                </div>
              )}

              {/* Scenario 3: Approved, rent added, balance_due > 0, not cancelled (exclude default repayment invoice) */}
              {paymentAdded && hasRentAdded && normalizedStatus === 'APPROVED' && balanceDue > 0 && !isCancelledStatus && !applicationData?.default_invoice && (
                <div className="bg-[#fef9ec] p-[10px] text-[14px] text-[#94845b] font-normal mt-5">
                  <b>Thank you for your business. Your account has been adjusted.</b>
                  <p className="mt-1">
                    Your Access Fee is being increased due to an update from your property regarding the new Rent Amount for {formatAmount(Number(applicationData?.rent_amount || 0))}. You owe: {formatAmount(balanceDue)}.<br />
                    This must be paid prior to your move in date.<br />
                    Please click the Purchase button to review your payment options.
                  </p>
                </div>
              )}

              {/* Scenario 4: Approved, rent added, balance_due < 0, not cancelled */}
              {paymentAdded && hasRentAdded && normalizedStatus === 'APPROVED' && balanceDue < 0 && !isCancelledStatus && (
                <div className="bg-[#fef9ec] p-[10px] text-[14px] text-[#94845b] font-normal mt-5">
                  <b>Thank you for your business. Your account has been adjusted.</b>
                  <p className="mt-1">Your balance due is {formatAmount(balanceDue)}.</p>
                </div>
              )}

              {/* Scenario 5: Approved, rent added, no balance_due, not cancelled, no fee invoice */}
              {paymentAdded && hasRentAdded && normalizedStatus === 'APPROVED' && !balanceDue && !isCancelledStatus && !hasApplicationFeeInvoice && (
                <div className="bg-[#fef9ec] p-[10px] text-[14px] text-[#94845b] font-normal mt-5">
                  <b>Thank you for your business.</b>
                  {applicationData?.progress_status === 'Policy Activation Pending' ? (
                    <p className="mt-1">
                      Your account is paid in full but the property still needs to activate your policy. Please reach out to them for more information.
                    </p>
                  ) : (
                    <p className="mt-1">Your account is paid in full.</p>
                  )}
                </div>
              )}
            </div>
          )
        })()}

        {/* Payment Log - Angular parity: only after this applicant has paid; hide during $35 purchase flow. */}
        {!isRetryTimerShow && !showPaymentForm && !paymentCompleted && isPaymentAdded(applicationData) && (
          <PaymentLogTable
            paymentHistory={applicationData.payment_history || []}
            paymentAdjustments={applicationData.payment_adjustment}
            paymentAndPaymentAdjustment={applicationData.payment_and_payment_adjustment}
            applicantId={applicantId ? String(applicantId) : undefined}
          />
        )}

        {showPaymentForm && !paymentCompleted && (
          <div className="space-y-6">
            {/* Payment window countdown (matches Angular: light yellow banner, dark gold text) */}
            {countdown && (
              <div className="bg-[#fff8e1] border border-[#faebcc] p-4 rounded-none shadow-none" role="alert">
                <p className="text-[#8a6d3b] text-[14px] leading-relaxed font-bold">
                  Please select your payment method in order to complete your application. {countdown}
                </p>
              </div>
            )}
            {/* Success Message */}
            {successMessage && (
              <div className="p-4 bg-green-50 border-l-4 border-green-400 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-green-800">{successMessage}</p>
                </div>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="p-4 bg-red-50 border-l-4 border-red-400 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-800">{stripHtmlTags(error)}</p>
                </div>
              </div>
            )}

            {/* Payment Form */}
            {paymentConfig && applicationData ? (
              <div>
                <PaymentForm
                  key={applicationData?.applicant_id} // Force re-render when applicant data changes
                  paymentConfig={paymentConfig}
                  applicationData={applicationData}
                  applicationFee={applicationFee || 0}
                  paymentType={currentPaymentType}
                  onPaymentSubmit={handlePaymentSubmit}
                  onPayPalPaymentSuccess={handlePayPalPaymentSuccess}
                  onPayPalPaymentError={handlePayPalPaymentError}
                  isLoading={isProcessingPayment}
                />
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-md border border-gray-200 p-4 sm:p-8">
                <div className="text-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
                  <p className="text-gray-600">
                    {!paymentConfig ? 'Loading payment configuration...' : APPLICANT_CONTENT.application.messages.loading}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Action Buttons - Return To Home and Back */}
        {!isRetryTimerShow && !showPaymentForm && !paymentCompleted && (
          <div className="flex flex-wrap gap-3 pt-4 pb-6">
            <button
              onClick={handleReturnToHome}
              disabled={isReturningHome}
              className="inline-flex items-center gap-2 px-8 py-3 bg-white text-[#1597ff] border-2 border-[#1597ff] font-semibold rounded-lg hover:bg-blue-50 transition-colors shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isReturningHome ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading...
                </>
              ) : (
                'Return To Home'
              )}
            </button>
            <button
              onClick={() => {
                const signaturePath = applicantId
                  ? getTabPathWithApplicantId('signature', applicantId)
                  : '/applicant/application/signature'
                if (applicantId) {
                  navigate(signaturePath, getTabNavigationOptions('signature'))
                } else {
                  navigate(signaturePath)
                }
              }}
              className="inline-flex items-center gap-2 bg-[#1597ff] hover:bg-[#0d7acc] text-white font-medium px-8 py-3 rounded-lg transition-all shadow-sm hover:shadow-md"
            >
              {APPLICANT_CONTENT.application.buttons.back}
            </button>
          </div>
        )}
      </div>

      <ValidationModal
        {...applicantValidationModalProps}
        isOpen={Boolean(validationModalMessage)}
        title={APPLICANT_CONTENT.errors.validationMessage}
        message={validationModalMessage ?? ""}
        onClose={closeValidationModal}
      />

      <ConfirmDialog
        {...applicantConfirmDialogProps}
        isOpen={showErrorModal && errorModalMessage.includes('Rent Amount has changed')}
        title={getPaymentErrorModalTitle(errorModalMessage)}
        message={formatPaymentErrorModalMessage(errorModalMessage)}
        primaryLabel="Refresh Page"
        onPrimary={() => {
          window.location.reload()
        }}
        secondaryLabel="Cancel"
        onSecondary={closePaymentErrorModal}
        onDismiss={closePaymentErrorModal}
      />

      <ValidationModal
        {...applicantValidationModalProps}
        isOpen={showErrorModal && !errorModalMessage.includes('Rent Amount has changed')}
        title={getPaymentErrorModalTitle(errorModalMessage)}
        message={formatPaymentErrorModalMessage(errorModalMessage)}
        showValidationIcon={false}
        onClose={closePaymentErrorModal}
      />
      </div>
    </div>
  )
}
